export interface ViewTabOption {
  /** Stable identifier — matches the `?tab=` URL value. */
  key: string;
  label: string;
}

export interface ViewTabsProps {
  options: ReadonlyArray<ViewTabOption>;
  current: string;
  onChange: (next: string) => void;
  /** ARIA label for the tablist wrapper (e.g. "Work items views"). */
  ariaLabel: string;
  /** Test-id prefix so each host page gets stable selectors. */
  testIdPrefix: string;
}

/**
 * Horizontal tab strip — one button per view (Board / Table / Timeline,
 * Table / Gallery, etc.). Uses ARIA `role="tablist"` + `role="tab"` so
 * screen readers announce it as a tab strip; the host page renders the
 * matching `role="tabpanel"` underneath.
 */
export function ViewTabs({
  options,
  current,
  onChange,
  ariaLabel,
  testIdPrefix
}: ViewTabsProps) {
  return (
    <nav
      className="pn-listctl-tabs"
      role="tablist"
      aria-label={ariaLabel}
      data-testid={`${testIdPrefix}-tabs`}
    >
      {options.map((opt) => {
        const active = opt.key === current;
        return (
          <button
            key={opt.key}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`${testIdPrefix}-tab-${opt.key}`}
            data-active={active}
            className="pn-listctl-tab"
            onClick={() => onChange(opt.key)}
          >
            {opt.label}
          </button>
        );
      })}
    </nav>
  );
}
